import React, { useRef, useState } from 'react';
import { X } from 'lucide-react';

export interface UnitNumberFieldProps {
  label: string;
  value?: string;
  onChange: (newValue: string | undefined) => void;
  units?: string[];
  defaultUnit?: string;
  min?: number;
  max?: number;
  step?: number;
  isModified?: boolean;
}

export function UnitNumberField({
  label,
  value,
  onChange,
  units = ['px', '%', 'rem', 'em', 'vh', 'vw', 'auto'],
  defaultUnit = 'px',
  min,
  max,
  step = 1,
  isModified,
}: UnitNumberFieldProps): React.JSX.Element {
  // Parse numeric part and unit part
  let numVal = '';
  let currentUnit = defaultUnit;

  if (value !== undefined && value !== null && value !== '') {
    const trimmed = String(value).trim();
    if (trimmed === 'auto') {
      numVal = 'auto';
      currentUnit = 'auto';
    } else {
      const match = trimmed.match(/^(-?\d*\.?\d+)\s*(px|%|rem|em|vh|vw)?$/);
      if (match) {
        numVal = match[1];
        currentUnit = match[2] || defaultUnit;
      } else {
        numVal = trimmed;
      }
    }
  }

  const [isScrubbing, setIsScrubbing] = useState(false);
  const dragStartRef = useRef<{ startX: number; startVal: number } | null>(null);

  const handlePointerDown = (e: React.PointerEvent) => {
    if (numVal === 'auto') return;
    const initialNum = parseFloat(numVal) || 0;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    setIsScrubbing(true);
    dragStartRef.current = { startX: e.clientX, startVal: initialNum };
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isScrubbing || !dragStartRef.current) return;
    const delta = e.clientX - dragStartRef.current.startX;
    const multiplier = e.shiftKey ? 10 : 1;
    let nextNum = dragStartRef.current.startVal + Math.round(delta * (step || 1) * multiplier);
    if (min !== undefined) nextNum = Math.max(min, nextNum);
    if (max !== undefined) nextNum = Math.min(max, nextNum);

    onChange(`${nextNum}${currentUnit === 'auto' ? 'px' : currentUnit}`);
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (isScrubbing) {
      try {
        (e.target as HTMLElement).releasePointerCapture(e.pointerId);
      } catch {
        // ignore
      }
      setIsScrubbing(false);
      dragStartRef.current = null;
    }
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.trim();
    if (raw === '') {
      onChange(undefined);
      return;
    }
    if (raw === 'auto') {
      onChange('auto');
      return;
    }
    const parsed = parseFloat(raw);
    if (!isNaN(parsed)) {
      onChange(`${parsed}${currentUnit === 'auto' ? 'px' : currentUnit}`);
    } else {
      onChange(raw);
    }
  };

  const handleUnitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newU = e.target.value;
    if (newU === 'auto') {
      onChange('auto');
    } else if (numVal && numVal !== 'auto') {
      onChange(`${numVal}${newU}`);
    } else {
      onChange(`0${newU}`);
    }
  };

  return (
    <div className="flex flex-col gap-1 text-[11px] select-none">
      <div className="flex items-center justify-between text-muted-foreground font-medium">
        <label
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          className="cursor-ew-resize hover:text-foreground flex items-center gap-1 leading-none py-0.5"
          title="Drag horizontally to scrub number (Hold Shift for ×10)"
        >
          {isModified && (
            <span
              className="inline-block w-1.5 h-1.5 rounded-full bg-[#3b97e3]"
              title="Modified in this state"
            />
          )}
          <span>{label}</span>
        </label>
        {isModified && (
          <button
            type="button"
            onClick={() => onChange(undefined)}
            className="text-muted-foreground/60 hover:text-rose-500 p-0.5 rounded cursor-pointer"
            title="Reset to default/inherited"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>

      <div className="flex items-center rounded border border-input bg-background overflow-hidden focus-within:border-[#3b97e3] focus-within:ring-1 focus-within:ring-[#3b97e3]">
        <input
          type="text"
          value={numVal}
          placeholder="auto"
          onChange={handleTextChange}
          className="w-full px-1.5 py-1 text-xs bg-transparent text-foreground font-mono focus:outline-none"
        />
        {units.length > 1 && (
          <select
            value={currentUnit}
            onChange={handleUnitChange}
            className="bg-muted/50 border-l border-input px-1 py-1 text-[10px] text-muted-foreground cursor-pointer focus:outline-none"
          >
            {units.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
        )}
      </div>
    </div>
  );
}

export interface IconRadioItem<T extends string> {
  value: T;
  label: string;
  icon?: React.ReactNode;
}

export interface IconRadioGroupProps<T extends string> {
  label: string;
  value?: T;
  options: IconRadioItem<T>[];
  onChange: (val: T | undefined) => void;
  isModified?: boolean;
}

export function IconRadioGroup<T extends string>({
  label,
  value,
  options,
  onChange,
  isModified,
}: IconRadioGroupProps<T>): React.JSX.Element {
  return (
    <div className="flex flex-col gap-1 text-[11px] select-none">
      <div className="flex items-center justify-between text-muted-foreground font-medium">
        <span className="flex items-center gap-1 leading-none py-0.5">
          {isModified && (
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#3b97e3]" />
          )}
          {label}
        </span>
        {isModified && (
          <button
            type="button"
            onClick={() => onChange(undefined)}
            className="text-muted-foreground/60 hover:text-rose-500 p-0.5 rounded cursor-pointer"
            title="Reset"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
      <div className="flex items-center bg-muted/40 p-0.5 rounded border border-input gap-0.5">
        {options.map((opt) => {
          const isActive = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(isActive ? undefined : opt.value)}
              className={`flex-1 flex items-center justify-center p-1 rounded text-xs transition-colors cursor-pointer ${
                isActive
                  ? 'bg-background text-[#3b97e3] font-bold shadow-xs border border-border'
                  : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
              }`}
              title={opt.label}
            >
              {opt.icon || opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export interface ColorPickerFieldProps {
  label: string;
  value?: string;
  onChange: (newColor: string | undefined) => void;
  isModified?: boolean;
}

export function ColorPickerField({
  label,
  value = '',
  onChange,
  isModified,
}: ColorPickerFieldProps): React.JSX.Element {
  const quickColors = ['#ffffff', '#000000', '#3b97e3', '#10b981', '#f59e0b', '#ef4444', 'transparent'];

  return (
    <div className="flex flex-col gap-1 text-[11px] select-none">
      <div className="flex items-center justify-between text-muted-foreground font-medium">
        <span className="flex items-center gap-1 leading-none py-0.5">
          {isModified && (
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#3b97e3]" />
          )}
          {label}
        </span>
        {isModified && (
          <button
            type="button"
            onClick={() => onChange(undefined)}
            className="text-muted-foreground/60 hover:text-rose-500 p-0.5 rounded cursor-pointer"
            title="Reset"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
      <div className="flex items-center gap-1.5">
        <div className="relative w-6 h-6 rounded border border-input shrink-0 overflow-hidden shadow-xs">
          <input
            type="color"
            value={value && value !== 'transparent' ? value : '#000000'}
            onChange={(e) => onChange(e.target.value)}
            className="absolute inset-0 w-8 h-8 -top-1 -left-1 cursor-pointer"
          />
        </div>
        <input
          type="text"
          value={value}
          placeholder="none"
          onChange={(e) => onChange(e.target.value || undefined)}
          className="flex-1 px-1.5 py-1 text-xs rounded border border-input bg-background text-foreground font-mono focus:outline-none focus:border-[#3b97e3]"
        />
      </div>
      {/* Quick Swatches */}
      <div className="flex items-center gap-1 mt-0.5">
        {quickColors.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => onChange(c)}
            className="w-4 h-4 rounded-full border border-border/80 shadow-xs cursor-pointer hover:scale-110 transition-transform"
            style={{ backgroundColor: c === 'transparent' ? '#ffffff' : c }}
            title={c}
          />
        ))}
      </div>
    </div>
  );
}
